/**
 * The join strip's two gates, and the three property writes that open it.
 *
 * The strip is server-rendered for EVERY visitor -- it has to be, because the home page is edge
 * cached and one visitor's render is served to the next -- so the markup ships collapsed
 * (`height: 0`, `overflow: hidden`, `visibility: hidden`, `aria-hidden`, all inline) and this
 * module is the only thing that decides, per browser, whether it opens. That is the same shape as
 * the rest of the cache-safe personalisation on this site; see the inline script at the end of
 * resources/views/partials/standings-home.blade.php.
 *
 * Two questions have to answer yes, in this order:
 *
 *   1. Is the visitor signed in?  urcAuth.js owns that question -- `window.urcAuth`, whose state
 *      is derived from an ID token the Auth0 SDK has already validated. `whenSignedIn()` there is
 *      reveal-only by construction, which is exactly this file's posture: it fires once, and never
 *      reports a sign-out. Note that the SDK answers asynchronously, so a single synchronous read
 *      would report "signed out" for a fan who is signed in -- the subscription is not optional.
 *   2. Have they NOT joined yet?  A loyalty API read, passed in as `resolveState` because the API
 *      client lives in the Preact tree. Only asked once question 1 says yes, so a signed-out
 *      visitor never triggers a call that could only fail.
 *
 * **The animation is in joinStrip.css, not here**, and that is the point of the split. This file
 * measures one number, writes one pixel height, and clears it again once the transition is over.
 * It reads no computed styles, listens for no transition events, and schedules no frames -- the
 * previous version did all three and each was a source of jank or of an outright bug.
 *
 * Reveal only, never collapse. partials/standings-home.blade.php needs its `else` branch because
 * the SERVER bakes a per-user class into cacheable HTML; the markup here is identical for
 * everyone, so no cache variant can arrive already-open and there is nothing to correct. The one
 * case deliberately not handled: signing out in another tab while this page sits open leaves the
 * strip up until the next navigation. It is a promo, not a gate.
 */

import { whenSignedIn } from './urcAuth';

/** The collapsed box. */
const WRAPPER_SELECTOR = '[data-join-strip]';

/** Both site headers. Absolutely positioned, so nothing in the flow accounts for them. */
const HEADER_SELECTOR = '#site-top-header, #site-banner';

/**
 * The hero's own header clearance, which the strip takes over once it is open.
 *
 * Above 1200px this is `.match-section { margin-top: 50px }` (modules/_home-hero.scss), sitting on
 * the hero's first child and collapsing out through the container; below that it is already
 * `unset`, so zeroing it there is a no-op and mobile is untouched.
 */
const HERO_CLEARANCE_SELECTOR = '.home-hero-container .match-section';

/**
 * Enrolment states that mean "do not offer this fan a join".
 *
 * `unconfigured` is in here because the tenant's master challenge is not provisioned, so there is
 * nothing to join yet -- the library's own guidance, and what JoinBlock already does. Everything
 * NOT listed opens the strip, which is the important half: `unknown` covers every failed read,
 * and failing open is the house position. mapEnrolment puts it plainly -- a host that hides the
 * strip from someone who could join is worse off than one told it cannot tell -- and a redundant
 * invitation costs nothing, since the CTA is an editor-set link rather than a write.
 */
const INELIGIBLE = [ 'enrolled', 'unconfigured' ];

/**
 * When to hand the box back to `height: auto`.
 *
 * **Must stay longer than the `transition: height` duration in joinStrip.css (300ms).** Settling
 * early would write `height: auto` mid-flight, which cancels the transition and snaps.
 *
 * A plain timer rather than `transitionend`, deliberately. `transitionend` bubbles -- LoyaltyJoin's
 * CTA has a background transition of its own that surfaces on this element -- and it does not fire
 * at all for a zero-length transition, under `transition: none`, or while the tab is backgrounded,
 * so it needs a timer as a fallback anyway. Keeping only the timer removes the event, both its
 * guards, and the two-paths-to-one-state problem, and it is correct in every one of those cases.
 */
const SETTLE_MS = 450;

/** Trailing debounce on resize, before the header clearance is re-measured. */
const RESIZE_DEBOUNCE_MS = 120;

/**
 * Teardown for the wiring below, or null when nothing is wired.
 *
 * Doubles as the once-per-page guard: a second init while one is live returns the same teardown
 * rather than attaching a second set of listeners.
 */
let teardown = null;

/**
 * The distance from the strip's top edge down to the bottom of the site header.
 *
 * Measured rather than written down, because it is three different numbers. Both headers are
 * `position: absolute`, so nothing in the flow accounts for them and `main`'s 56px padding-top
 * does not reach the bottom of the stack: at 1200px and up the stack ends at 107 (51 below main's
 * content top), below that at 56 (0), and when the smart app banner is showing
 * `body.has-app-banner` pushes it to 136 (80). All three measured at localhost:8080. See
 * docs/subsystems/page-templates.md.
 *
 * Both values are viewport-relative, so the difference is right at any scroll position. And the
 * wrapper's own top never moves -- the clearance is applied as padding INSIDE it, not as a margin
 * on it -- so this needs no correction for what it last returned.
 *
 * @param {HTMLElement} wrapper The strip's outer box.
 * @return {number} Clearance in whole pixels.
 */
function headroom( wrapper ) {
	const top = wrapper.getBoundingClientRect().top;

	let bottom = top;

	// A display:none header (the top bar, below 1200px) reports an all-zero rect, which loses the
	// max -- and that is exactly right.
	document.querySelectorAll( HEADER_SELECTOR ).forEach( header => {
		bottom = Math.max( bottom, header.getBoundingClientRect().bottom );
	} );

	return Math.max( 0, Math.round( bottom - top ) );
}

/**
 * Push the band clear of the header, from inside the collapsed box.
 *
 * Padding on the INNER element rather than a margin on the wrapper, and that choice is what keeps
 * the reveal to a single animated property. A margin on the wrapper is outside the animated box:
 * it occupies space while the strip is still `height: 0`, so it has to be animated too, and two
 * simultaneous motions were most of what made the old reveal look jerky. Padding on the inner is
 * inside the box, so `overflow: hidden` clips it to nothing while closed and it is simply part of
 * the height the strip grows to.
 *
 * Padding and not a margin on the inner either: a top margin there would collapse out of the
 * wrapper the moment [data-join-strip-settled] sets `overflow: visible` and the wrapper stops
 * being a block formatting context, moving the whole strip down by the clearance at the exact
 * moment the animation ends.
 *
 * @param {HTMLElement} wrapper The strip's outer box.
 * @param {HTMLElement} inner   The mount the Preact tree rendered into.
 * @return {void}
 */
function applyHeadroom( wrapper, inner ) {
	inner.style.paddingTop = `${ headroom( wrapper ) }px`;
}

/**
 * Open the strip. Three writes now, and one more when the transition is over.
 *
 * Everything about HOW it moves is in joinStrip.css: the duration, the easing, what
 * [data-join-strip-settled] means geometrically, how the hero unwinds its own header clearance
 * in step, and reduced motion.
 *
 * @param {HTMLElement} wrapper The strip's outer box.
 * @param {HTMLElement} inner   The mount the Preact tree rendered into.
 * @return {void}
 */
function open( wrapper, inner ) {
	// Back into the tab order and the accessibility tree before anything moves. `visibility` is
	// managed inline only -- the server ships it hidden, so clearing it here is what reveals it.
	wrapper.style.visibility = '';
	wrapper.removeAttribute( 'aria-hidden' );
	wrapper.dataset.joinStripOpen = '';

	// Hand the hero's header clearance over to the strip, which is now carrying it -- left alone it
	// is a 50px gap between the two on desktop, and nothing below 1200px where it is already unset.
	//
	// An inline write rather than a state-predicated CSS rule. Both work; this one needs no
	// specificity argument against the theme's own `.home-hero-container .match-section`, and it
	// keeps the flush behaviour beside the other state changes instead of splitting it across two
	// files. It still animates -- joinStrip.css declares the transition on this element, and a
	// transition fires on an inline change just as it does on a cascade change.
	const heroClearance = document.querySelector( HERO_CLEARANCE_SELECTOR );

	if ( heroClearance ) {
		heroClearance.style.marginTop = '0px';
	}

	// One synchronous layout read, so `height: 0` is committed as the transition's start value
	// even if something else touched this element in the same task.
	void wrapper.offsetHeight;

	// offsetHeight is the border box, so this already includes the clearance padding above.
	wrapper.style.height = `${ inner.offsetHeight }px`;

	setTimeout( () => {
		// BOTH inline properties have to go, not just the height. The partial ships `height` and
		// `overflow` inline so the strip is collapsed before joinStrip.css loads, and an inline
		// declaration outranks any selector — leaving `overflow: hidden` behind means the
		// [data-join-strip-settled] rule's `overflow: visible` never applies and the CTA's focus
		// ring stays clipped. Measured: it did.
		wrapper.dataset.joinStripSettled = '';
		wrapper.style.height = '';
		wrapper.style.overflow = '';
	}, SETTLE_MS );
}

/**
 * Wires the strip to both gates.
 *
 * Called from JoinStripBlock's mount effect, so the Preact tree already exists inside the
 * collapsed box by the time anything here runs.
 *
 * @param {() => Promise<string>} resolveState Resolves the fan's EnrolmentState. Must not reject
 *                                             -- a failed read is 'unknown', not a rejection.
 * @return {() => void} Teardown. Drops the auth subscription and the resize listener; it does NOT
 *                      close a strip that has already opened, because the box is server-rendered
 *                      markup that outlives the Preact tree mounted inside it.
 */
export function initJoinStripReveal( resolveState ) {
	if ( teardown ) {
		return teardown;
	}

	const wrapper = document.querySelector( WRAPPER_SELECTOR );
	const inner = wrapper && wrapper.firstElementChild;

	if ( ! inner ) {
		return () => {};
	}

	// Applied up front rather than at reveal: it is clipped to nothing while the box is closed, so
	// it costs nothing to have it already right when the height is set.
	applyHeadroom( wrapper, inner );

	let opened = false;
	let state = null;

	/** One read per page, however many times the auth signal fires. */
	const enrolmentState = () => {
		if ( ! state ) {
			state = resolveState();
		}

		return state;
	};

	/**
	 * Gate two. Reached only once gate one has said yes.
	 *
	 * @param {string} resolved The fan's EnrolmentState.
	 */
	function decide( resolved ) {
		// Always recorded, whichever way it went: this attribute is the whole of the debugging
		// story for "the strip is not showing and I do not know why".
		wrapper.dataset.joinStripState = resolved;

		if ( opened || INELIGIBLE.includes( resolved ) ) {
			return;
		}

		opened = true;
		open( wrapper, inner );
	}

	// Gate one. Fires once, synchronously if the SDK has already settled a fan; urcAuth.js owns
	// the subscription, and the auth bundle owns the focus/pageshow/visibilitychange re-checks and
	// the cross-tab channel that get it answered otherwise.
	const unwatchAuth = whenSignedIn( () => {
		if ( ! opened ) {
			enrolmentState().then( decide );
		}
	} );

	/*
	 * The clearance is three numbers and the viewport decides which. A trailing debounce rather
	 * than requestAnimationFrame: rAF callbacks do not run while the tab is not being rendered, so
	 * a viewport change made in the background -- a rotated phone, a tab restored at another size
	 * -- would never be picked up.
	 *
	 * Nothing else needs re-measuring. While closed the padding is clipped; once settled the box
	 * is `height: auto` and follows it natively. The only window where this is briefly wrong is
	 * the 450ms of the transition itself, and the settle clears the stale height at the end of it.
	 */
	let timer = 0;

	const onResize = () => {
		window.clearTimeout( timer );
		timer = window.setTimeout( () => applyHeadroom( wrapper, inner ), RESIZE_DEBOUNCE_MS );
	};

	window.addEventListener( 'resize', onResize );

	teardown = () => {
		teardown = null;
		unwatchAuth();
		window.clearTimeout( timer );
		window.removeEventListener( 'resize', onResize );
	};

	return teardown;
}
